// How ELVA's work is counted (D227): the minutes a person spends on each task. Mock only.
// One shared store, so ELVA's work can read the same numbers. A change counts from that day on;
// past months are not recounted (the Report's past periods stay as they were counted).
import { useSyncExternalStore } from 'react';
import { recordSettingsChange } from './settingsEdit';

export type CountedTaskKey =
  | 'claims_sent'
  | 'posted'
  | 'eob_claims'
  | 'portal'
  | 'denials'
  | 'preauths'
  | 'appeals'
  | 'bank_diffs'
  | 'insurance_check';

export interface CountedTask {
  key: CountedTaskKey;
  task: string;
  /** ELVA's default, in minutes. */
  defaultMin: number;
}

export const COUNTED_MIN = 1;
export const COUNTED_MAX = 240;

export const COUNTED_TASKS: readonly CountedTask[] = [
  { key: 'claims_sent', task: 'A claim prepared and sent', defaultMin: 10 },
  { key: 'posted', task: 'A payment posted', defaultMin: 5 },
  { key: 'eob_claims', task: 'A claim from a paper EOB', defaultMin: 8 },
  { key: 'portal', task: 'A portal visit', defaultMin: 4 },
  { key: 'denials', task: 'A denial or underpayment found', defaultMin: 10 },
  { key: 'preauths', task: 'A pre-auth prepared', defaultMin: 15 },
  { key: 'appeals', task: 'An appeal sent', defaultMin: 45 },
  { key: 'bank_diffs', task: 'A bank difference worked out', defaultMin: 15 },
  { key: 'insurance_check', task: 'An insurance check before a visit', defaultMin: 5 },
];

let minutes: Record<CountedTaskKey, number> = Object.fromEntries(
  COUNTED_TASKS.map((t) => [t.key, t.defaultMin]),
) as Record<CountedTaskKey, number>;
let version = 0;
const listeners = new Set<() => void>();
function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export const COUNTED_FOOTER_ID = 'how-counted';

export function getCountedMinutes(): Readonly<Record<CountedTaskKey, number>> {
  return minutes;
}
export function useCountedMinutes(): Readonly<Record<CountedTaskKey, number>> {
  return useSyncExternalStore(subscribe, () => minutes, () => minutes);
}

function set(key: CountedTaskKey, next: number): void {
  const t = COUNTED_TASKS.find((x) => x.key === key);
  if (!t) return;
  const from = minutes[key];
  minutes = { ...minutes, [key]: next };
  version++;
  recordSettingsChange(COUNTED_FOOTER_ID, `${t.task}: ${String(from)} to ${String(next)} min`);
  for (const l of listeners) l();
}
export function setCountedMinutes(key: CountedTaskKey, next: number): void {
  set(key, Math.max(COUNTED_MIN, Math.min(COUNTED_MAX, Math.round(next))));
}
export function resetCountedMinutes(key: CountedTaskKey): void {
  const t = COUNTED_TASKS.find((x) => x.key === key);
  if (t && minutes[key] !== t.defaultMin) set(key, t.defaultMin);
}
export function getCountedVersion(): number {
  return version;
}
